import { VisualisationContextProps } from 'widgets/supportVisualisation/Content/VisualisationContentProps';
import React, { useState } from 'react';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import orderBy from 'lodash/orderBy';
import { Grid } from '@instinet-analytics/semantic-ui-components';
import { ContactSupportRequestState } from 'communication/types/ContactSupportRequest';
import StreamPanel from './StreamPanel';
import './Streams.scss';
import StreamFilters, {
    applyFilter,
    getChannels,
    StreamsFilter,
} from './StreamFilters';
import {
    safeDateTime,
    toDateString,
} from 'widgets/supportVisualisation/SupportVisualisationHelpers';
import { GetRowClassParams } from '@instinet-analytics/semantic-ui-components';
import ShowHideToggle from 'utils/components/ShowHideToggle/ShowHideToggle';
import { ChannelInfo } from 'communication/Channel';

export interface StreamInfo {
    rowId: number;
    channelId: number | undefined;
    channelIdDisp: string;
    destination: string;
    requestId: number | undefined;
    requestIdDisp: string;
    correlationId: string;
    time: Date;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: any;
    payloadLength: number | undefined;
    isLatest: boolean;
    delta: number;
    channelDelta: number;
    type: 'Request' | 'Response';
    messageNumber?: number;
    responses?: string;
}

export const StreamStatusDummyDestination = 'Stream Status';

export default function Streams(props: VisualisationContextProps) {
    const { state, width } = props;

    const [selectedMessage, setSelectedMessage] = useState<
        StreamInfo | undefined
    >(undefined);
    const [showPanel, setShowPanel] = useState(false);
    const [collapseFilters, setCollapseFilters] = useState(false);
    const [collapseGrid, setCollapseGrid] = useState(false);

    const [filter, setFilter] = useState<StreamsFilter>({
        channelIds: [],
        onlyLatestRequests: false,
        includeRpcChannels: false,
        destinations: [],
        type: 'both',
        quickSearch: '',
    });

    if (!state?.streams?.channels) {
        return <div>None found in state</div>;
    }

    const rowData = extractMessages(state, filter);

    const columnDefs = getColumns();

    const onSelectedRowIdsChanged = (rowDataIds: string[]) => {
        if (rowDataIds.length !== 1) {
            setSelectedMessage(undefined);
            setShowPanel(false);
        }
        const row = rowData.find((r) => r.rowId.toString() === rowDataIds[0]);
        setSelectedMessage(row);
        setShowPanel(true);
    };

    const onUpdateFilter = (nextFilter: StreamsFilter) => {
        setFilter(nextFilter);
    };

    return (
        <div className={'Streams'}>
            {!collapseFilters && (
                <StreamFilters
                    filter={filter}
                    onUpdateFilter={onUpdateFilter}
                    state={state}
                />
            )}

            <ShowHideToggle
                className="collapse-filters"
                status={collapseFilters ? 'show' : 'hide'}
                hideLabel={true}
                tooltip={`${collapseFilters ? 'Show' : 'Hide'} filters`}
                onToggle={() => {
                    setCollapseFilters(!collapseFilters);
                }}
            />
            <div className="data-panel">
                {!collapseGrid && (
                    <div className="grid-container">
                        <Grid
                            autoSizeColumns={true}
                            singleSelectOnly={true}
                            columnDefs={columnDefs}
                            rowData={rowData}
                            rowClassRules={getRowClassRules()}
                            getRowDataId={(r) => r.rowId.toString()}
                            onSelectedRowIdsChanged={onSelectedRowIdsChanged}
                        />
                        <ShowHideToggle
                            className="collapse-grid"
                            status={collapseGrid ? 'show' : 'hide'}
                            hideLabel={true}
                            tooltip={`${collapseGrid ? 'Show' : 'Hide'} grid`}
                            onToggle={() => {
                                setCollapseGrid(!collapseGrid);
                            }}
                        />
                    </div>
                )}
                {selectedMessage && showPanel && (
                    <div className="stream-panel-container">
                        {collapseGrid && (
                            <ShowHideToggle
                                className="collapse-grid"
                                status={collapseGrid ? 'show' : 'hide'}
                                hideLabel={true}
                                tooltip={`${
                                    collapseGrid ? 'Show' : 'Hide'
                                } grid`}
                                onToggle={() => {
                                    setCollapseGrid(!collapseGrid);
                                }}
                            />
                        )}
                        <StreamPanel
                            stream={selectedMessage}
                            width={width}
                            onClickClose={() => {
                                setShowPanel(false);
                                setCollapseGrid(false);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

function extractMessages(
    state: ContactSupportRequestState,
    filter: StreamsFilter
): StreamInfo[] {
    const messages: StreamInfo[] = [];
    let rowId = 0;

    const extractChannelMessages = (
        channels: ChannelInfo[],
        suffix?: string
    ) => {
        for (const channel of channels) {
            const { id: channelId } = channel;
            for (
                let requestIndex = 0;
                requestIndex < channel.latestRequests.length;
                requestIndex++
            ) {
                const request = channel.latestRequests[requestIndex];
                const {
                    id: requestId,
                    destination,
                    payload,
                    correlationId,
                    sentTime: time,
                } = request;

                const suffixDisp = suffix ? `(c-${suffix})` : '';

                messages.push({
                    rowId: rowId++,
                    channelId,
                    destination,
                    requestId,
                    channelIdDisp: `${channelId} ${suffixDisp}`,
                    requestIdDisp: `${requestId} ${suffixDisp}`,
                    correlationId,
                    time,
                    payload,
                    payloadLength: JSON.stringify(payload ?? '').length,
                    isLatest: requestIndex === 0,
                    type: 'Request',
                    delta: 0,
                    channelDelta: 0,
                    responses: `${request.receivedCount} First ${toDateString(
                        request.firstReceivedTime
                    )} - Last ${toDateString(request.lastReceivedTime)}`,
                });

                for (
                    let responseIndex = 0;
                    responseIndex < request.latestResponses.length;
                    responseIndex++
                ) {
                    const response = request.latestResponses[responseIndex];
                    const {
                        payload: responsePayload,
                        receivedTime: responseTime,
                        messageNumber,
                    } = response;

                    messages.push({
                        rowId: rowId++,
                        channelId,
                        destination,
                        requestId,
                        channelIdDisp: `${channelId} ${suffixDisp}`,
                        requestIdDisp: `${requestId} ${suffixDisp}`,
                        time: responseTime,
                        correlationId,
                        payload: responsePayload,
                        payloadLength: JSON.stringify(responsePayload ?? '')
                            .length,
                        isLatest: requestIndex === 0,
                        type: 'Response',
                        delta: 0,
                        channelDelta: 0,
                        messageNumber,
                    });
                }
            }
        }
    };

    const currentChannels = getChannels(state, filter);
    extractChannelMessages(currentChannels);

    const disposedConnections = orderBy(
        state?.streams?.disposedConnections ?? [],
        ['disposedDate'],
        ['desc']
    );
    if (disposedConnections.length > 0) {
        for (let i = 0; i < disposedConnections.length; i++) {
            const dispCon = disposedConnections[i];
            extractChannelMessages(dispCon.channels, ((i + 1) * -1).toString());
        }
    }

    for (const upr of state?.streams?.unprocessedResponses ?? []) {
        const time = upr.receivedTime;
        let destination = upr.reason;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyMessage = upr.message as any;
        const { channelId, requestId, payload, correlationId } =
            extractDetailsFromUnprocessedMessage(anyMessage);
        if (channelId !== undefined) {
            destination = upr.reason;
        }

        messages.push({
            rowId: rowId++,
            channelId,
            destination,
            requestId,
            channelIdDisp: channelId.toString(),
            requestIdDisp: requestId.toString(),
            correlationId,
            time,
            payload,
            payloadLength: JSON.stringify(payload ?? '').length,
            isLatest: true,
            type: 'Response',
            delta: 0,
            channelDelta: 0,
            messageNumber: upr.messageNumber,
        });
    }

    for (const ssm of state?.streams?.streamStatusMessages ?? []) {
        const time = ssm.receivedTime;

        messages.push({
            rowId: rowId++,
            channelId: undefined,
            destination: StreamStatusDummyDestination,
            time,
            channelIdDisp: '',
            requestIdDisp: '',
            payload: ssm.message,
            payloadLength: JSON.stringify(ssm.message ?? '').length,
            isLatest: true,
            type: 'Response',
            delta: 0,
            channelDelta: 0,
            correlationId: '',
            requestId: undefined,
        });
    }

    const orderedMessages = orderBy(messages, ['time'], ['desc']);

    adornOrderedMessagesWithDeltas(orderedMessages);

    return applyFilter(orderedMessages, filter);
}

function extractDetailsFromUnprocessedMessage(anyMessage: unknown) {
    const channelId = extractFromMessage(anyMessage, 'channelId') ?? -9999;
    const requestId = extractFromMessage(anyMessage, 'requestId') ?? -9999;
    const payload = extractFromMessage(anyMessage, 'payload');
    const correlationId = extractFromMessage(anyMessage, 'correlationId');

    return { channelId, requestId, payload, correlationId };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractFromMessage(anyMessage: any, propName: string) {
    const value = anyMessage[propName];
    if (value !== undefined) {
        return value;
    }
    if (
        anyMessage.details &&
        Array.isArray(anyMessage.details) &&
        anyMessage.details.length > 0
    ) {
        if (propName === 'payload') {
            return anyMessage.details[0];
        }
        return anyMessage.details[0][propName];
    }
    return undefined;
}

function getColumns(): ColDef[] {
    return [
        {
            field: 'time',
            headerName: 'Time',
            resizable: true,
            width: 80,
            cellRenderer: (props: { value: Date }) => {
                return <span>{toDateString(props.value)}</span>;
            },
        },
        {
            field: 'channelIdDisp',
            headerName: 'Channel Id',
            width: 70,
        },
        { field: 'type', headerName: 'Type', width: 60, sortable: true },
        {
            field: 'requestIdDisp',
            headerName: 'Request Id',
            width: 80,
        },
        {
            field: 'messageNumber',
            headerName: 'Message Number',
            width: 80,
        },
        {
            field: 'destination',
            headerName: 'Destination',
            resizable: true,
            width: 150,
        },
        {
            field: 'delta',
            headerName: 'Ms',
            resizable: true,
            width: 70,
        },
        {
            field: 'channelDelta',
            headerName: 'Ms (Channel)',
            resizable: true,
            width: 70,
        },
        {
            field: 'payloadLength',
            headerName: 'Payload Length',
            width: 70,
        },
        {
            field: 'payload',
            headerName: 'Payload',
            resizable: true,
            cellRenderer: (props: { value: string | Date }) => {
                return (
                    <span>
                        {typeof props.value === 'string'
                            ? props.value
                            : JSON.stringify(props.value)}
                    </span>
                );
            },
        },
    ];
}

function adornOrderedMessagesWithDeltas(orderedMessages: StreamInfo[]) {
    if (orderedMessages.length <= 1) {
        return;
    }
    const channelPrevs: { [key: string]: StreamInfo } = {};
    const channelPrevKey = (i: StreamInfo) =>
        `ch:${i.channelId} r: ${i.requestId}`;

    for (let i = orderedMessages.length - 2; i >= 0; i--) {
        const message = orderedMessages[i];

        const prev = orderedMessages[i + 1];
        message.delta = calculateDelta(prev, message);

        const csKey = channelPrevKey(message);

        let channelPrev = channelPrevs[csKey];
        if (channelPrev) {
            message.channelDelta = calculateDelta(channelPrev, message);
        }

        channelPrev = channelPrevs[csKey] = message;
    }
}

function calculateDelta(from: StreamInfo, to: StreamInfo): number {
    const fromDt = safeDateTime(from.time);
    const toDt = safeDateTime(to.time);

    return toDt.diff(fromDt, 'milliseconds').milliseconds;
}

function getRowClassRules() {
    return {
        request: (params: GetRowClassParams<StreamInfo>) => {
            const item: StreamInfo = params.data;
            return item.type === 'Request';
        },
        response: (params: GetRowClassParams<StreamInfo>) => {
            const item: StreamInfo = params.data;
            return item.type === 'Response';
        },
    };
}
